/**
 * ANALYZE → REVIEW → CONFIRM → OUTCOME.
 *
 * The convention this breaks is an "Approve" button that means an unexamined
 * mixture of accepted, committed, scheduled and implemented. Here the four
 * facts are separate all the way through, and the reader is told which one
 * they are about to create before they create it.
 *
 * The commit is a governed project decision, so it is not a small green
 * button beside the analysis. It has a review step that says what will be
 * recorded and what will not be written, and a confirmation the human has to
 * give deliberately — the backend 428s without it, and that refusal is
 * honoured rather than pre-empted by sending `confirmed: true` on the first
 * click.
 *
 * The OUTCOME is not modelled here. F-AUTONOMY-0 owns that vocabulary and
 * this contract's response is one of the two shapes it reads; a second
 * outcome component would be a second visual language for the same facts.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { preconControlPlaneApi } from "../../services/intelligenceService";
import { classifyWorkflowError } from "../../lib/workflowErrors";
import { createSequencer } from "../../lib/sequencer";
import { ContractMismatchCallout } from "../runtime/RuntimePrimitives";
import DecisionOutcomePanel from "../autonomy/DecisionOutcomePanel";
import {
  PRECON_CONTRACT,
  PRECON_COPY,
  canCommitPrecon,
  presentPreconAnalysis,
  presentCommitmentPreview,
  presentCommitRefusal,
  presentAnalysisFailure,
  readCommitError,
} from "./preconPresentation";
import { ClipboardCheck, Loader2, AlertTriangle, Ban, RefreshCw, Lock } from "lucide-react";

const READINESS_TONE = {
  ready: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  blocked: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  unknown: "border-border text-muted-foreground bg-muted/40",
};

const Caveat = ({ children, testId }) =>
  children ? (
    <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid={testId}>
      {children}
    </p>
  ) : null;

const PreconCommitPanel = ({ projectId, user }) => {
  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [approvedByName, setApprovedByName] = useState("");
  const [note, setNote] = useState("");

  const analyzeSeq = useRef(createSequencer()).current;
  const commitSeq = useRef(createSequencer()).current;
  /**
   * A synchronous in-flight guard. `committing` is React state, so two clicks
   * inside one tick both read it as false and both send — which is exactly
   * what happened before this ref existed. The request schema forbids extra
   * keys and carries no idempotency key, so duplicate prevention has to
   * happen here; inventing a key the backend never defined would be worse
   * than the bug.
   */
  const commitInFlight = useRef(false);
  /** Set once a commitment is actually recorded against this proposal. */
  const committedRef = useRef(false);

  /**
   * Every piece of per-project state is dropped when the project changes. A
   * commitment response for project A rendering under project B would invite
   * a reader to believe they committed something they never saw.
   */
  useEffect(() => {
    analyzeSeq.next();
    commitSeq.next();
    setAnalysis(null);
    setAnalysisError(null);
    setRefusal(null);
    setOutcome(null);
    setConfirming(false);
    setApprovedByName("");
    setNote("");
    commitInFlight.current = false;
    committedRef.current = false;
  }, [projectId, analyzeSeq, commitSeq]);

  const analyze = useCallback(async () => {
    const ticket = analyzeSeq.next();
    // A new analysis invalidates any commit in flight: its proposal id is
    // about to be superseded.
    commitSeq.next();
    setLoading(true);
    setAnalysis(null);
    setAnalysisError(null);
    setRefusal(null);
    setOutcome(null);
    // A fresh analysis is a fresh proposal, so a commitment becomes
    // legitimate again.
    commitInFlight.current = false;
    committedRef.current = false;
    try {
      const data = await preconControlPlaneApi.analyze(projectId);
      if (analyzeSeq.isCurrent(ticket)) setAnalysis(data);
    } catch (err) {
      if (analyzeSeq.isCurrent(ticket)) setAnalysisError(classifyWorkflowError(err));
    } finally {
      if (analyzeSeq.isCurrent(ticket)) setLoading(false);
    }
  }, [projectId, analyzeSeq, commitSeq]);

  const view = presentPreconAnalysis(analysis);
  const preview = presentCommitmentPreview(view);
  const mayCommit = canCommitPrecon(user);

  const commit = async () => {
    if (!view?.proposalId || commitInFlight.current) return;
    commitInFlight.current = true;
    const ticket = commitSeq.next();
    setCommitting(true);
    setRefusal(null);
    try {
      const data = await preconControlPlaneApi.commit(projectId, {
        proposalId: view.proposalId,
        // Given deliberately, by this call and nowhere else.
        confirmed: true,
        approvedByName: approvedByName.trim() || undefined,
        note: note.trim() || undefined,
      });
      if (!commitSeq.isCurrent(ticket)) return;
      setOutcome(data);
      setConfirming(false);
      // Deliberately NOT released here. One proposal, one commitment: the
      // dialog closes but React unmounts it a frame later, and a fast second
      // click landing in that gap would record a second approval of the same
      // proposal. The guard reopens when a new analysis runs or the project
      // changes — which is exactly when a new commitment becomes legitimate.
      committedRef.current = true;
    } catch (err) {
      if (!commitSeq.isCurrent(ticket)) return;
      // A coded refusal is a governed workflow state; anything else is a
      // transport failure. Neither is retried automatically.
      const coded = readCommitError(err);
      setRefusal(presentCommitRefusal(coded || classifyWorkflowError(err)));
      setConfirming(false);
    } finally {
      // Released only where a retry is legitimate — a refusal or a transport
      // failure recorded nothing, so the reader must be able to try again.
      if (!committedRef.current) commitInFlight.current = false;
      if (commitSeq.isCurrent(ticket)) setCommitting(false);
    }
  };

  return (
    <Card className="border-border" data-testid="precon-commit">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-primary" />
          {PRECON_COPY.sectionTitle}
        </CardTitle>
        <p className="text-xs text-muted-foreground leading-relaxed">{PRECON_COPY.sectionLead}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {!analysis && !analysisError ? (
          <Button size="sm" onClick={analyze} disabled={loading} data-testid="run-analysis">
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
            )}
            {PRECON_COPY.analyzeAction}
          </Button>
        ) : null}

        {analysisError ? (
          <div className="space-y-1" data-testid="analysis-error">
            <p className="text-xs text-muted-foreground">
              {presentAnalysisFailure(analysisError)?.headline}
            </p>
            <Caveat>{presentAnalysisFailure(analysisError)?.detail}</Caveat>
            {presentAnalysisFailure(analysisError)?.retryable ? (
              <Button size="sm" variant="outline" onClick={analyze} disabled={loading}>
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                {PRECON_COPY.refreshAction}
              </Button>
            ) : null}
          </div>
        ) : null}

        {view ? (
          <>
            <ContractMismatchCallout received={view.contractVersion} supported={PRECON_CONTRACT} />

            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={`text-[10px] ${READINESS_TONE[view.readiness.tone] || READINESS_TONE.unknown}`}
                data-testid="precon-readiness"
              >
                {view.readiness.label}
              </Badge>
              <span className="text-[10px] font-mono text-muted-foreground" data-testid="proposal-id">
                {view.proposalId}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs ml-auto"
                onClick={analyze}
                disabled={loading}
                data-testid="rerun-analysis"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                {PRECON_COPY.refreshAction}
              </Button>
            </div>
            <Caveat testId="readiness-meaning">{view.readiness.meaning}</Caveat>
            <Caveat testId="proposal-only">{view.proposalOnlyNote}</Caveat>

            {view.blockers.length ? (
              <section className="space-y-1" data-testid="precon-blockers">
                <p className="text-[11px] font-medium flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {PRECON_COPY.blockersTitle}
                </p>
                {view.blockers.map((blocker, index) => (
                  <p key={index} className="text-[10px] text-muted-foreground leading-relaxed">
                    · {blocker}
                  </p>
                ))}
              </section>
            ) : null}

            {/* WHAT YOU ARE AUTHORIZING — stated before the click */}
            {preview && view.committable ? (
              <section className="space-y-2 rounded border border-border px-3 py-2" data-testid="commit-review">
                <p className="text-[11px] font-medium">{PRECON_COPY.reviewTitle}</p>
                <p className="text-[10px] text-muted-foreground">
                  {preview.packageCount} package{preview.packageCount === 1 ? "" : "s"} ·{" "}
                  {preview.proposedChangeCount} proposed change
                  {preview.proposedChangeCount === 1 ? "" : "s"} · critical path of{" "}
                  {preview.criticalPathLength}
                </p>
                <div className="space-y-0.5">
                  <p className="text-[10px] font-medium">{PRECON_COPY.willRecordTitle}</p>
                  {preview.willRecord.map((entry, index) => (
                    <p key={index} className="text-[10px] text-muted-foreground leading-relaxed">
                      · {entry}
                    </p>
                  ))}
                </div>
                <div className="space-y-0.5" data-testid="will-not-do">
                  <p className="text-[10px] font-medium flex items-center gap-1.5">
                    <Ban className="w-3 h-3" />
                    {PRECON_COPY.willNotTitle}
                  </p>
                  {preview.willNotDo.map((entry, index) => (
                    <p key={index} className="text-[10px] text-muted-foreground leading-relaxed">
                      · {entry}
                    </p>
                  ))}
                </div>
                <Caveat testId="applied-locked">{preview.appliedLockedNote}</Caveat>

                {mayCommit ? (
                  <Button
                    size="sm"
                    onClick={() => setConfirming(true)}
                    disabled={committing}
                    data-testid="open-commit"
                  >
                    {PRECON_COPY.commitAction}
                  </Button>
                ) : (
                  <p className="text-[10px] text-muted-foreground flex items-start gap-1.5" data-testid="commit-authority">
                    <Lock className="w-3 h-3 mt-0.5 shrink-0" />
                    <span className="leading-relaxed">
                      Recording a preconstruction commitment is reserved for project managers,
                      superintendents and admins. You can read everything here.
                    </span>
                  </p>
                )}
              </section>
            ) : null}
          </>
        ) : null}

        {/* A GOVERNED REFUSAL — never a retry, never a transport failure */}
        {refusal ? (
          <Alert className="border-amber-500/40 bg-amber-500/10" data-testid="commit-refusal">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="space-y-1">
              <p className="text-[11px] font-medium" data-testid="refusal-headline">
                {refusal.headline}
              </p>
              {refusal.detail ? (
                <p className="text-[10px] leading-relaxed">{refusal.detail}</p>
              ) : null}
              {refusal.message ? (
                <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid="refusal-message">
                  {refusal.message}
                </p>
              ) : null}
              {refusal.blockers?.length ? (
                <div className="space-y-0.5" data-testid="refusal-blockers">
                  {refusal.blockers.map((blocker, index) => (
                    <p key={index} className="text-[10px] leading-relaxed">
                      · {blocker}
                    </p>
                  ))}
                </div>
              ) : null}
              {refusal.kind === "STALE_PROPOSAL" ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={analyze}
                  disabled={loading}
                  data-testid="refresh-after-stale"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  {PRECON_COPY.refreshAction}
                </Button>
              ) : null}
              <Caveat testId="never-auto-retry">{refusal.neverAutoRetry}</Caveat>
            </AlertDescription>
          </Alert>
        ) : null}

        {/* THE OUTCOME — F-AUTONOMY-0's model, not a second one */}
        {outcome ? (
          <DecisionOutcomePanel response={outcome} onDismiss={() => setOutcome(null)} />
        ) : null}
      </CardContent>

      {/* CONFIRMATION — deliberate, and never sent on the first click */}
      <Dialog open={confirming} onOpenChange={(next) => !committing && setConfirming(next)}>
        <DialogContent className="max-w-md" data-testid="commit-confirm">
          <DialogHeader>
            <DialogTitle className="text-base">{PRECON_COPY.confirmTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs leading-relaxed" data-testid="confirm-body">
              {PRECON_COPY.confirmBody}
            </p>
            <div className="space-y-1">
              <Label htmlFor="precon-name" className="text-xs">
                {PRECON_COPY.nameLabel}
              </Label>
              <Input
                id="precon-name"
                value={approvedByName}
                onChange={(event) => setApprovedByName(event.target.value)}
                maxLength={255}
                data-testid="approved-by-name"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="precon-note" className="text-xs">
                {PRECON_COPY.noteLabel}
              </Label>
              <Textarea
                id="precon-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={2000}
                rows={3}
                data-testid="commit-note"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={commit} disabled={committing} data-testid="confirm-commit">
                {committing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                {PRECON_COPY.confirmAction}
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirming(false)}
                disabled={committing}
                data-testid="cancel-commit"
              >
                {PRECON_COPY.cancelAction}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PreconCommitPanel;
